import { QueryClient } from '@tanstack/react-query'

import { PaginatedData } from 'src/queries/entity.types'
import { isListQuery, updatePaginatedEntity } from 'src/queries/utils'
import { BuyerOrder, BuyerOrderItem, SellerOrder, SellerOrderItem } from 'src/types'

import { mergeOrderItems } from './mergeOrderItems'

type Order = SellerOrder | BuyerOrder

interface UpdateOrderItemInOrderCacheArgs<OrderType extends Order> {
  newOrderItem: OrderType extends SellerOrder ? SellerOrderItem : BuyerOrderItem
  queryClient: QueryClient
  queryKey: string
}

export const updateOrderItemInOrderCache = <OrderType extends Order>({
  newOrderItem,
  queryClient,
  queryKey,
}: UpdateOrderItemInOrderCacheArgs<OrderType>) => {
  const [orderQueryKey, existingOrder] =
    queryClient.getQueriesData<OrderType>({
      type: 'active',
      exact: false,
      queryKey: [queryKey, newOrderItem.orderId],
    })?.[0] ?? []

  if (existingOrder) {
    queryClient.setQueryData<OrderType>(orderQueryKey, {
      ...existingOrder,
      items: existingOrder.items.map(orderItem => {
        if (orderItem.id === newOrderItem.id) {
          return mergeOrderItems({
            newOrderItem,
            oldOrderItem: orderItem,
          })
        }

        return orderItem
      }),
    })
  }

  const [ordersQueryKey, existingOrders] =
    queryClient.getQueriesData<PaginatedData<OrderType>>({
      type: 'active',
      exact: false,
      queryKey: [queryKey],
      predicate: isListQuery(queryKey),
    })?.[0] ?? []

  if (existingOrders) {
    const orderToUpdate = existingOrders.pages
      .flatMap(({ results }) => results)
      .find(order => order.id === newOrderItem.orderId)

    if (orderToUpdate) {
      const updatedOrder = {
        ...orderToUpdate,
        items: orderToUpdate.items.map(orderItem => {
          if (orderItem.id === newOrderItem.id) {
            return mergeOrderItems({
              newOrderItem,
              oldOrderItem: orderItem,
            })
          }

          return orderItem
        }),
      }

      const updatedOrders = updatePaginatedEntity({
        entity: updatedOrder,
        entityPages: existingOrders,
      })

      queryClient.setQueryData(ordersQueryKey, updatedOrders)
    }
  }
}
